import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, of } from 'rxjs';
import type { Observable } from 'rxjs';

import { ApiUrlHelper } from '../config/api-url-helper';
import { NotificationType } from '../enums/common-enum';
import { AppService } from './app.service';
import { ImpersonationService } from './impersonation.service';
import { OnboardingStateService } from './onboarding-state.service';
import { StorageKey, StorageService } from './storage.service';
import { TokenRefreshService } from './token-refresh.service';
import type { AuthSession, LoginOtpState } from '../model/auth-model';

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private readonly router = inject(Router);
  private readonly apiUrl = inject(ApiUrlHelper);
  private readonly appService = inject(AppService);
  private readonly onboardingState = inject(OnboardingStateService);
  private readonly impersonation = inject(ImpersonationService);
  private readonly tokenRefreshService = inject(TokenRefreshService);

  readonly loginOtp = signal<LoginOtpState | null>(null);
  readonly session = signal<AuthSession | null>(null);

  constructor(private readonly storage: StorageService) {
    this.loadFromStorage();
  }

  loadFromStorage(): void {
    this.loginOtp.set(this.storage.getValue(StorageKey.loginOtp) as LoginOtpState | null);
    this.session.set(this.storage.getValue(StorageKey.authSession) as AuthSession | null);
  }

  saveLoginOtp(state: LoginOtpState): void {
    this.storage.setValue(StorageKey.loginOtp, state);
    this.loginOtp.set(state);
  }

  getLoginOtp(): LoginOtpState | null {
    this.loadFromStorage();
    const stored = this.loginOtp();
    if (!stored) {
      return null;
    }

    const userId = Number(stored.userId);
    const uniqueId = String(stored.uniqueId ?? '').trim();

    if (!Number.isFinite(userId) || userId <= 0 || !uniqueId) {
      return null;
    }

    return {
      ...stored,
      userId,
      uniqueId,
    };
  }

  clearLoginOtp(): void {
    this.storage.removeValue(StorageKey.loginOtp);
    this.loginOtp.set(null);
  }

  saveSession(session: AuthSession): void {
    this.storage.setValue(StorageKey.authSession, session);
    this.session.set(session);
  }

  getSession(): AuthSession | null {
    return this.session();
  }

  clearSession(): void {
    this.storage.removeValue(StorageKey.authSession);
    this.session.set(null);
  }

  clearAuth(): void {
    this.clearLoginOtp();
    this.clearSession();
    this.storage.removeValue(StorageKey.loginData);
    this.storage.setPersistentSession(false);
    this.onboardingState.setAdminSession(false);
    this.impersonation.clear();
  }

  logout(): void {
    // withCredentials (set by doPost) sends the refresh-token cookie, so the server can end the session even
    // if the access token has already expired, and lets the browser apply the cookie deletion.
    this.appService
      .doPost(this.apiUrl.apiUrl.account.logout, {})
      .pipe(
        finalize(() => {
          this.clearAuth();
          void this.router.navigate(['/login']);
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.appService.showNotification('Logout', response.message, NotificationType.SUCCESS);
          }
        },
      });
  }

  isAuthenticated(): boolean {
    const current = this.session();
    if (!current?.accessToken) {
      return false;
    }

    const expiry = Date.parse(current.expiry);
    return !Number.isNaN(expiry) && expiry > Date.now();
  }

  /**
   * Route guards call this instead of isAuthenticated() directly: an expired
   * access token doesn't mean the session is over, only that it needs a
   * refresh via the still-valid refresh-token cookie.
   */
  ensureAuthenticated(): Observable<boolean> {
    if (this.isAuthenticated()) {
      return of(true);
    }

    const current = this.session();
    if (!current?.accessToken) {
      return of(false);
    }

    return this.tokenRefreshService.refreshAccessToken().pipe(
      map((newAccessToken) => {
        if (!newAccessToken) {
          return false;
        }
        this.loadFromStorage();
        return true;
      }),
      // Refresh unreachable (offline, 5xx): a guard can't wait it out, so treat as signed out for this navigation.
      catchError(() => of(false))
    );
  }
}
